import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  getNotas,
  nuevaNota,
  saveNota,
  updateNota,
  deleteNota,
} from "../application/notas";

export const NotasContext = createContext();

// Fecha local en formato YYYY-MM-DD (no usamos toISOString para no correr el
// dia por la zona horaria).
export const hoyISO = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Las tres mutaciones son optimistas: primero se toca el estado local (la UI
// responde en el acto) y despues sale la escritura a Firestore. Si falla, se
// deshace el cambio y se propaga el error para que la pantalla avise.
// Antes cada accion esperaba el write MAS una relectura completa de la
// coleccion, y eso era ~1s de espera por cada clic en fijar o marcar hecha.
const NotasContextProvider = ({ children }) => {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Espejo de `notas` para poder guardar el valor previo al revertir sin
  // leer estado adentro de un updater.
  const notasRef = useRef([]);
  useEffect(() => {
    notasRef.current = notas;
  }, [notas]);

  const refreshNotas = useCallback(async () => {
    setLoading(true);
    try {
      setNotas(await getNotas());
    } catch (error) {
      console.error("Error al cargar notas:", error);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshNotas();
  }, [refreshNotas]);

  const crearNota = useCallback(async (datos) => {
    const nota = nuevaNota(datos);
    setNotas((prev) => [nota, ...prev]);
    try {
      await saveNota(nota);
      return nota;
    } catch (error) {
      console.error("Error al crear la nota:", error);
      setNotas((prev) => prev.filter((n) => n.idFirebase !== nota.idFirebase));
      throw error;
    }
  }, []);

  const editarNota = useCallback(async (id, patch) => {
    const previo = notasRef.current.find((n) => n.idFirebase === id);
    const cambios = { ...patch, updatedAt: new Date().toISOString() };
    setNotas((prev) =>
      prev.map((n) => (n.idFirebase === id ? { ...n, ...cambios } : n))
    );
    try {
      await updateNota(id, cambios);
    } catch (error) {
      console.error("Error al editar la nota:", error);
      if (previo) {
        setNotas((prev) => prev.map((n) => (n.idFirebase === id ? previo : n)));
      }
      throw error;
    }
  }, []);

  const borrarNota = useCallback(async (id) => {
    const previo = notasRef.current.find((n) => n.idFirebase === id);
    setNotas((prev) => prev.filter((n) => n.idFirebase !== id));
    try {
      await deleteNota(id);
    } catch (error) {
      console.error("Error al borrar la nota:", error);
      if (previo) setNotas((prev) => [previo, ...prev]);
      throw error;
    }
  }, []);

  // Notas pendientes cuyo recordatorio ya llego (o vencio). Alimenta el badge
  // del sidebar.
  const notasVencidas = useMemo(() => {
    const hoy = hoyISO();
    return notas.filter(
      (n) => n.estado !== "HECHA" && n.recordatorio && n.recordatorio <= hoy
    );
  }, [notas]);

  const notasPendientes = useMemo(
    () => notas.filter((n) => n.estado !== "HECHA"),
    [notas]
  );

  const value = useMemo(
    () => ({
      notas,
      loading,
      initialLoaded,
      refreshNotas,
      crearNota,
      editarNota,
      borrarNota,
      notasPendientes,
      notasVencidas,
    }),
    [
      notas,
      loading,
      initialLoaded,
      refreshNotas,
      crearNota,
      editarNota,
      borrarNota,
      notasPendientes,
      notasVencidas,
    ]
  );

  return <NotasContext.Provider value={value}>{children}</NotasContext.Provider>;
};

export default NotasContextProvider;
